/**
 * An `<img>` that behaves well on a phone.
 *
 * Three things it does that a bare tag does not. It loads lazily unless it is
 * told it sits above the fold, so the picture a visitor is actually looking at
 * is not queued behind the forty below it. It offers a `srcset` so a phone takes
 * a phone-sized file. And it keeps the bundled-artwork fallback that
 * `onImageError` already gave the cards.
 *
 * `priority` marks the one image that is the page's largest paint, usually a
 * full-bleed hero. That one loads eagerly, at high fetch priority, and decodes
 * synchronously so it paints in the first frame it can.
 */
import { onImageError } from '@/lib/imageFallback';
import { CARD_WIDTHS, HERO_WIDTHS, srcSetFor } from '@/lib/images';

type NativeImgProps = Omit<React.ComponentPropsWithoutRef<'img'>, 'decoding' | 'fetchPriority'>;

export interface ImgProps extends NativeImgProps {
    src: string;
    /** Above the fold, and the largest thing on screen: load it first. */
    priority?: boolean;
    /** Bundled artwork to swap in when the remote file cannot be loaded. */
    fallback?: string;
    /** Widths to offer; defaults to card widths, or hero widths when `priority`. */
    widths?: readonly number[];
    /** The layout slot's width, for the browser to pick against. */
    sizes?: string;
    /** Forwarded for the hero parallax, which measures the element it moves. */
    ref?: React.Ref<HTMLImageElement>;
}

export function Img({
    src,
    priority = false,
    fallback,
    widths,
    sizes,
    alt = '',
    onError,
    ref,
    loading,
    ...rest
}: ImgProps) {
    const srcSet = srcSetFor(src, widths ?? (priority ? HERO_WIDTHS : CARD_WIDTHS));
    return (
        <img
            {...rest}
            ref={ref}
            src={src}
            srcSet={srcSet}
            // Without a hint the browser assumes the slot is the full viewport and
            // takes a file wider than a card ever needs.
            sizes={
                srcSet
                    ? (sizes ?? (priority ? '100vw' : '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw'))
                    : undefined
            }
            alt={alt}
            // `loading="eager"` on its own is for the things above the fold that
            // are not the largest paint, such as the header logo: fetch them with
            // the page, but do not let them outbid the hero.
            loading={loading ?? (priority ? 'eager' : 'lazy')}
            decoding={priority ? 'sync' : 'async'}
            fetchPriority={priority ? 'high' : undefined}
            onError={onError ?? (fallback ? onImageError(fallback) : undefined)}
        />
    );
}

export default Img;
